// Master Sheet — the screen's own strings (28_MasterSheet.jsx, MasterSheetChanges.jsx) in the portal's four
// languages (2026-10-21). The sheet's COLUMN headings are not here: they come from the server (SP_MASTER_LABELS)
// and stay as the PT team's workbook names them, which is why the sentences below quote them in English.
const en = {
  subtitle: "The PT team's 32 columns, rebuilt from SAP and NetSuite, plus Classification, Currency and Price source",
  columnsCount: '{{n}} columns',
  columnsHidden: '{{n}} columns — {{h}} hidden',
  syncSap: 'Sync SAP',
  syncingSap: 'Syncing SAP…',
  syncNs: 'Sync NetSuite',
  syncingNs: 'Syncing NetSuite…',
  addRow: 'Add a row',
  columns: 'Columns',
  hiddenTag: '· {{n}} hidden',
  showColumns: 'Show columns',
  showAll: 'Show all',
  destNames: 'Destination names',
  checkChanges: 'Check changes',
  checkChangesHint: 'What changed on the sheet since you last marked it as checked',
  building: 'Building…',
  exportExcel: 'Export Excel',
  intro: "The PT team's workbook rebuilt from the ledgers: the same 32 columns, in the same order, under the same header names, with <b>Classification</b>, <b>Currency</b> and <b>Price source</b> added at the end — the POs behind these rows are raised in USD, KRW, JPY and EUR, so the price columns need their unit beside them, and a finished-goods PO carries no price in SAP, so where one is shown the sheet says which SFP PO it was copied from. The file is a <b>column spec</b> — nothing is imported from it. One row is one outbound order line: on the SAP side an SO line joined to its PO line, on the NetSuite side an NS line, plus SAP POs that have no sales order yet.",
  legendManualHead: '{{n}} columns you type — 100% by hand.',
  legendManualBody: 'Neither SAP nor NetSuite carries these at all. Press Edit on a row to fill them.',
  legendHybridHead: '{{n}} columns shared.',
  legendHybridBody: 'The system fills what it has and locks that cell; where it has nothing, the cell is yours.',
  legendSystemHead: '{{n}} columns from the system.',
  legendSystemBody: 'SAP and NetSuite own these — an empty one means the ledger has no value yet, not that somebody forgot.',
  newRow: 'New row',
  viewRow: 'View {{key}}',
  editRow: 'Edit {{key}}',
  viewOnly: 'View only',
  subView: 'Every field as the sheet holds it. Edit is beside View on the row.',
  subNew: 'A line that exists in no ledger. It is saved with a <c>MAN-…</c> key of its own and is highlighted from then on, so a typed row is never mistaken for one SAP sent. Nothing is required — fill in whatever you have.',
  subManual: 'A row somebody typed, so every field can be changed.',
  subLedger: 'The greyed fields come from SAP or NetSuite and are shown so you can tell which row this is. <b>Cycle No</b> and <b>Customer PO#</b> are missing from the form when the ledger already has them — an import owns those and would overwrite anything typed here. <b>Season</b> is always yours: no import writes it.',
  nextAfter: 'Next after {{po}}',
  nextAfterGuessed: 'Next after {{po}} — series guessed, check it',
  sysHas: 'The system has {{v}}',
  sysHasCpoTyped: 'The system has {{v}} — this row was typed over it; clear the box to go back to it',
  sysHasDateTyped: 'The system has {{v}} — this row was typed over it',
  sysNoCpo: 'The system has no customer PO for this line',
  sysNoDate: 'The system has no date for this line — PO, sales order and bill all carry none',
  deleteRow: 'Delete row',
  close: 'Close',
  cancel: 'Cancel',
  saving: 'Saving…',
  saveRow: 'Save row',
  saveChanges: 'Save changes',
  searchPh: 'Search HNX PO / SAP code / SFP / Item# / barcode / description…',
  srcAll: 'All sources',
  srcManual: 'Typed by hand',
  srcPlanned: 'Planned — no PO yet',
  rowsOf: '{{n}} of {{total}} rows',
  clearSortFilters: 'Clear sort & filters',
  thNo: 'No',
  thAction: 'Action',
  typedSysHas: 'Typed in. The system has {{v}}',
  typedNoDate: 'Typed in. The system has no date for this line',
  typedNoCpo: 'Typed in. The system has no customer PO for this line',
  lockedHint: 'From the ledger — an import owns this one',
  plannedChip: 'Planned · no PO yet',
  view: 'View',
  viewHint: 'View this row (read only)',
  plannedHint: 'Planned in Capacity Management, not yet raised in SAP or NetSuite — change it in the Capacity workbook; it updates here.',
  fromCapacity: 'From Capacity',
  edit: 'Edit',
  split: 'Split',
  splitTitle: 'Split this line by destination',
  splitEditHint: 'Edit how this line is split',
  del: 'Del',
  noRows: 'No rows',
  subtotal: 'Subtotal · {{cur}}',
  subtotalNoCur: 'Subtotal · no currency',
  subtotalRows: '{{n}} rows',
  loadingMore: 'Loading more…',
  loading: 'Loading…',
  destShared: 'shared with SAP Ship Doc — one name, both screens',
  destScope: 'the Master Sheet',
  delAsk: 'Delete this row?',
  delBody: 'A row somebody typed is the only kind that can be deleted, and it goes completely — the values on it are not kept anywhere else.',
  delConfirm: 'Delete the row',
  lineQty: 'Line quantity',
  allocated: 'Allocated',
  balanced: 'Balanced',
  leftToAllocate: 'Left to allocate',
  overBy: 'Over by',
  thDestination: 'Destination',
  thQuantity: 'Quantity',
  thStock: 'Stock',
  stockHint: 'Quantity with no destination yet',
  notSplit: 'Not split yet — this line is one row.',
  addLine: 'Add a line',
  putOnStock: 'Put the remaining {{n}} on stock',
  mustAddUp: 'The lines must add up to {{n}} before this can be saved.',
  saveLines: 'Save {{n}} lines',
  removeSplit: 'Remove the split',
  readFailed: 'Read failed ({{status}})',
  exportFailed: 'Export failed ({{status}})',
  splitOpenFailed: 'Could not open the split ({{status}})',
  saveFailed: 'Save failed ({{status}})',
  deleteFailed: 'Delete failed ({{status}})',
  syncFailed: 'Sync failed ({{status}})',
  soSyncFailed: 'SO sync failed ({{status}})',
  chgNoun: 'sheet',
  chgReading: 'Reading the whole sheet… this takes about five seconds.',
  chgGroupAll: 'All factories',
  chgSearch: 'Search HNX PO, SAP code, description, customer PO…',
  chgColFactory: 'Factory',
  chgColSapCode: 'SAP code',
  chgColDescription: 'Description',
  chgColDestination: 'Destination',
  chgColMonth: 'Month',
  chgPcs: '{{n}} pcs',
};

const ko = {
  subtitle: 'PT팀의 32개 열을 SAP와 NetSuite 데이터로 재구성하고 Classification, Currency, Price source 열을 더했습니다',
  columnsCount: '{{n}}개 열',
  columnsHidden: '{{n}}개 열 — {{h}}개 숨김',
  syncSap: 'SAP 동기화',
  syncingSap: 'SAP 동기화 중…',
  syncNs: 'NetSuite 동기화',
  syncingNs: 'NetSuite 동기화 중…',
  addRow: '행 추가',
  columns: '열 선택',
  hiddenTag: '· {{n}}개 숨김',
  showColumns: '표시할 열',
  showAll: '모두 표시',
  destNames: '도착지 이름',
  checkChanges: '변경 사항 확인',
  checkChangesHint: '마지막으로 확인 완료를 표시한 이후 시트에서 바뀐 내용',
  building: '생성 중…',
  exportExcel: 'Excel 내보내기',
  intro: 'PT팀의 워크북을 원장 데이터로 재구성한 것입니다: 같은 32개 열을 같은 순서, 같은 헤더 이름으로 두고 끝에 <b>Classification</b>, <b>Currency</b>, <b>Price source</b>를 더했습니다 — 이 행들의 PO는 USD, KRW, JPY, EUR로 발행되므로 가격 열 옆에 통화 단위가 필요하고, 완제품 PO는 SAP에 가격이 없으므로 가격이 표시된 경우 어느 SFP PO에서 복사했는지 시트가 알려 줍니다. 그 파일은 <b>열 사양</b>일 뿐이며, 파일에서 가져오는 데이터는 없습니다. 한 행은 출고 주문 라인 하나입니다: SAP 쪽은 SO 라인과 그 PO 라인을 연결한 것, NetSuite 쪽은 NS 라인 하나, 그리고 아직 판매오더가 없는 SAP PO입니다.',
  legendManualHead: '직접 입력하는 열 {{n}}개 — 100% 수기 입력.',
  legendManualBody: 'SAP와 NetSuite 어디에도 없는 값입니다. 행의 수정 버튼을 눌러 채우세요.',
  legendHybridHead: '공유 열 {{n}}개.',
  legendHybridBody: '시스템이 가진 값은 시스템이 채우고 그 셀을 잠급니다. 시스템에 값이 없으면 그 셀은 직접 입력합니다.',
  legendSystemHead: '시스템 열 {{n}}개.',
  legendSystemBody: 'SAP와 NetSuite가 관리하는 열입니다 — 비어 있다면 누가 잊은 것이 아니라 원장에 아직 값이 없다는 뜻입니다.',
  newRow: '새 행',
  viewRow: '{{key}} 보기',
  editRow: '{{key}} 수정',
  viewOnly: '보기 전용',
  subView: '시트에 저장된 그대로의 모든 필드입니다. 수정 버튼은 행의 보기 버튼 옆에 있습니다.',
  subNew: '어떤 원장에도 없는 라인입니다. 고유한 <c>MAN-…</c> 키로 저장되고 이후 계속 강조 표시되므로, 직접 입력한 행이 SAP에서 온 행으로 오인되지 않습니다. 필수 항목은 없습니다 — 아는 것만 입력하세요.',
  subManual: '직접 입력한 행이므로 모든 필드를 수정할 수 있습니다.',
  subLedger: '회색 필드는 SAP 또는 NetSuite에서 온 값으로, 어느 행인지 알아볼 수 있도록 표시됩니다. <b>Cycle No</b>와 <b>Customer PO#</b>는 원장에 이미 값이 있으면 양식에 나타나지 않습니다 — 가져오기가 관리하는 값이라 여기서 입력해도 덮어쓰이기 때문입니다. <b>Season</b>은 언제나 직접 입력합니다: 어떤 가져오기도 이 값을 쓰지 않습니다.',
  nextAfter: '{{po}} 다음 번호',
  nextAfterGuessed: '{{po}} 다음 번호 — 번호 체계를 추정했으니 확인하세요',
  sysHas: '시스템 값: {{v}}',
  sysHasCpoTyped: '시스템 값: {{v}} — 이 행은 직접 입력한 값으로 덮어썼습니다. 칸을 비우면 시스템 값으로 돌아갑니다',
  sysHasDateTyped: '시스템 값: {{v}} — 이 행은 직접 입력한 값으로 덮어썼습니다',
  sysNoCpo: '시스템에 이 라인의 고객 PO가 없습니다',
  sysNoDate: '시스템에 이 라인의 날짜가 없습니다 — PO, 판매오더, 청구서 모두 날짜가 없습니다',
  deleteRow: '행 삭제',
  close: '닫기',
  cancel: '취소',
  saving: '저장 중…',
  saveRow: '행 저장',
  saveChanges: '변경 사항 저장',
  searchPh: 'HNX PO / SAP 코드 / SFP / Item# / 바코드 / 품명 검색…',
  srcAll: '모든 출처',
  srcManual: '직접 입력',
  srcPlanned: '계획 — 아직 PO 없음',
  rowsOf: '{{total}}행 중 {{n}}행',
  clearSortFilters: '정렬 및 필터 지우기',
  thNo: '번호',
  thAction: '작업',
  typedSysHas: '직접 입력함. 시스템 값: {{v}}',
  typedNoDate: '직접 입력함. 시스템에 이 라인의 날짜가 없습니다',
  typedNoCpo: '직접 입력함. 시스템에 이 라인의 고객 PO가 없습니다',
  lockedHint: '원장 값 — 가져오기가 관리합니다',
  plannedChip: '계획 · 아직 PO 없음',
  view: '보기',
  viewHint: '이 행 보기 (읽기 전용)',
  plannedHint: 'Capacity Management에서 계획되었고 아직 SAP나 NetSuite에서 발행되지 않았습니다 — Capacity 워크북에서 수정하면 여기에 반영됩니다.',
  fromCapacity: 'Capacity에서',
  edit: '수정',
  split: '분할',
  splitTitle: '이 라인을 도착지별로 분할',
  splitEditHint: '이 라인의 분할 수정',
  del: '삭제',
  noRows: '행이 없습니다',
  subtotal: '소계 · {{cur}}',
  subtotalNoCur: '소계 · 통화 없음',
  subtotalRows: '{{n}}행',
  loadingMore: '더 불러오는 중…',
  loading: '불러오는 중…',
  destShared: 'SAP Ship Doc과 공유 — 이름 하나가 두 화면에 적용됩니다',
  destScope: 'Master Sheet',
  delAsk: '이 행을 삭제할까요?',
  delBody: '직접 입력한 행만 삭제할 수 있으며, 완전히 삭제됩니다 — 행의 값은 다른 곳에 보관되지 않습니다.',
  delConfirm: '행 삭제',
  lineQty: '라인 수량',
  allocated: '배분',
  balanced: '일치',
  leftToAllocate: '남은 배분',
  overBy: '초과',
  thDestination: '도착지',
  thQuantity: '수량',
  thStock: '재고',
  stockHint: '아직 도착지가 없는 수량',
  notSplit: '아직 분할하지 않았습니다 — 이 라인은 한 행입니다.',
  addLine: '라인 추가',
  putOnStock: '남은 {{n}}개를 재고로',
  mustAddUp: '라인 합계가 {{n}}이 되어야 저장할 수 있습니다.',
  saveLines: '{{n}}개 라인 저장',
  removeSplit: '분할 해제',
  readFailed: '불러오기 실패 ({{status}})',
  exportFailed: '내보내기 실패 ({{status}})',
  splitOpenFailed: '분할을 열 수 없습니다 ({{status}})',
  saveFailed: '저장 실패 ({{status}})',
  deleteFailed: '삭제 실패 ({{status}})',
  syncFailed: '동기화 실패 ({{status}})',
  soSyncFailed: 'SO 동기화 실패 ({{status}})',
  chgNoun: '시트',
  chgReading: '시트 전체를 읽는 중… 약 5초 걸립니다.',
  chgGroupAll: '모든 공장',
  chgSearch: 'HNX PO, SAP 코드, 품명, 고객 PO 검색…',
  chgColFactory: '공장',
  chgColSapCode: 'SAP 코드',
  chgColDescription: '품명',
  chgColDestination: '도착지',
  chgColMonth: '월',
  chgPcs: '{{n}}개',
};

const vi = {
  subtitle: '32 cột của team PT, dựng lại từ SAP và NetSuite, thêm Classification, Currency và Price source',
  columnsCount: '{{n}} cột',
  columnsHidden: '{{n}} cột — {{h}} cột đang ẩn',
  syncSap: 'Đồng bộ SAP',
  syncingSap: 'Đang đồng bộ SAP…',
  syncNs: 'Đồng bộ NetSuite',
  syncingNs: 'Đang đồng bộ NetSuite…',
  addRow: 'Thêm dòng',
  columns: 'Cột',
  hiddenTag: '· {{n}} ẩn',
  showColumns: 'Hiện cột',
  showAll: 'Hiện tất cả',
  destNames: 'Tên điểm đến',
  checkChanges: 'Kiểm tra thay đổi',
  checkChangesHint: 'Những gì đã thay đổi trên bảng kể từ lần gần nhất bạn đánh dấu đã kiểm tra',
  building: 'Đang tạo…',
  exportExcel: 'Xuất Excel',
  intro: 'Workbook của team PT được dựng lại từ dữ liệu sổ (ledger): cùng 32 cột, cùng thứ tự, cùng tên tiêu đề, thêm <b>Classification</b>, <b>Currency</b> và <b>Price source</b> ở cuối — các PO đứng sau những dòng này được phát hành bằng USD, KRW, JPY và EUR nên cột giá cần có đơn vị tiền bên cạnh, và PO thành phẩm không có giá trong SAP nên khi có giá hiển thị, bảng sẽ ghi rõ giá được lấy từ SFP PO nào. File gốc chỉ là <b>đặc tả cột</b> — không nhập dữ liệu nào từ file đó. Mỗi dòng là một dòng đơn hàng xuất: phía SAP là dòng SO nối với dòng PO của nó, phía NetSuite là một dòng NS, cộng thêm các SAP PO chưa có đơn bán hàng.',
  legendManualHead: '{{n}} cột bạn tự nhập — 100% bằng tay.',
  legendManualBody: 'Cả SAP lẫn NetSuite đều không có các giá trị này. Bấm Sửa trên một dòng để điền.',
  legendHybridHead: '{{n}} cột dùng chung.',
  legendHybridBody: 'Hệ thống điền những gì nó có và khóa ô đó; ô nào hệ thống không có thì do bạn điền.',
  legendSystemHead: '{{n}} cột từ hệ thống.',
  legendSystemBody: 'SAP và NetSuite quản lý các cột này — ô trống nghĩa là sổ dữ liệu chưa có giá trị, không phải do ai đó quên điền.',
  newRow: 'Dòng mới',
  viewRow: 'Xem {{key}}',
  editRow: 'Sửa {{key}}',
  viewOnly: 'Chỉ xem',
  subView: 'Mọi trường đúng như bảng đang lưu. Nút Sửa nằm cạnh nút Xem trên dòng.',
  subNew: 'Một dòng không có trong sổ dữ liệu nào. Dòng được lưu với mã <c>MAN-…</c> riêng và luôn được tô sáng từ đó, để dòng nhập tay không bao giờ bị nhầm với dòng do SAP gửi. Không có trường bắt buộc — có gì điền nấy.',
  subManual: 'Dòng do người nhập tay, nên mọi trường đều sửa được.',
  subLedger: 'Các trường màu xám lấy từ SAP hoặc NetSuite và được hiển thị để bạn nhận ra đây là dòng nào. <b>Cycle No</b> và <b>Customer PO#</b> không có trong form khi sổ dữ liệu đã có giá trị — lần nhập dữ liệu quản lý các trường này và sẽ ghi đè mọi thứ nhập ở đây. <b>Season</b> luôn do bạn điền: không lần nhập nào ghi vào trường này.',
  nextAfter: 'Số tiếp theo sau {{po}}',
  nextAfterGuessed: 'Số tiếp theo sau {{po}} — dãy số là suy đoán, hãy kiểm tra lại',
  sysHas: 'Hệ thống ghi nhận {{v}}',
  sysHasCpoTyped: 'Hệ thống ghi nhận {{v}} — dòng này đã được nhập tay đè lên; xóa ô để quay về giá trị của hệ thống',
  sysHasDateTyped: 'Hệ thống ghi nhận {{v}} — dòng này đã được nhập tay đè lên',
  sysNoCpo: 'Hệ thống không có customer PO cho dòng này',
  sysNoDate: 'Hệ thống không có ngày cho dòng này — PO, đơn bán hàng và hóa đơn đều không có',
  deleteRow: 'Xóa dòng',
  close: 'Đóng',
  cancel: 'Hủy',
  saving: 'Đang lưu…',
  saveRow: 'Lưu dòng',
  saveChanges: 'Lưu thay đổi',
  searchPh: 'Tìm HNX PO / mã SAP / SFP / Item# / barcode / mô tả…',
  srcAll: 'Tất cả nguồn',
  srcManual: 'Nhập tay',
  srcPlanned: 'Dự kiến — chưa có PO',
  rowsOf: '{{n}} / {{total}} dòng',
  clearSortFilters: 'Bỏ sắp xếp & lọc',
  thNo: 'STT',
  thAction: 'Thao tác',
  typedSysHas: 'Đã nhập tay. Hệ thống ghi nhận {{v}}',
  typedNoDate: 'Đã nhập tay. Hệ thống không có ngày cho dòng này',
  typedNoCpo: 'Đã nhập tay. Hệ thống không có customer PO cho dòng này',
  lockedHint: 'Từ sổ dữ liệu — lần nhập dữ liệu quản lý ô này',
  plannedChip: 'Dự kiến · chưa có PO',
  view: 'Xem',
  viewHint: 'Xem dòng này (chỉ đọc)',
  plannedHint: 'Đã lên kế hoạch trong Capacity Management, chưa phát hành trong SAP hay NetSuite — hãy sửa trong workbook Capacity; ở đây sẽ cập nhật theo.',
  fromCapacity: 'Từ Capacity',
  edit: 'Sửa',
  split: 'Tách',
  splitTitle: 'Tách dòng này theo điểm đến',
  splitEditHint: 'Sửa cách tách dòng này',
  del: 'Xóa',
  noRows: 'Không có dòng nào',
  subtotal: 'Tổng phụ · {{cur}}',
  subtotalNoCur: 'Tổng phụ · không có tiền tệ',
  subtotalRows: '{{n}} dòng',
  loadingMore: 'Đang tải thêm…',
  loading: 'Đang tải…',
  destShared: 'dùng chung với SAP Ship Doc — một tên, cả hai màn hình',
  destScope: 'Master Sheet',
  delAsk: 'Xóa dòng này?',
  delBody: 'Chỉ dòng nhập tay mới xóa được, và dòng bị xóa hoàn toàn — các giá trị trên dòng không được lưu ở đâu khác.',
  delConfirm: 'Xóa dòng',
  lineQty: 'Số lượng dòng',
  allocated: 'Đã phân bổ',
  balanced: 'Đã khớp',
  leftToAllocate: 'Còn phải phân bổ',
  overBy: 'Vượt',
  thDestination: 'Điểm đến',
  thQuantity: 'Số lượng',
  thStock: 'Tồn kho',
  stockHint: 'Số lượng chưa có điểm đến',
  notSplit: 'Chưa tách — dòng này vẫn là một dòng.',
  addLine: 'Thêm dòng',
  putOnStock: 'Đưa {{n}} còn lại vào tồn kho',
  mustAddUp: 'Tổng các dòng phải bằng {{n}} thì mới lưu được.',
  saveLines: 'Lưu {{n}} dòng',
  removeSplit: 'Bỏ tách',
  readFailed: 'Đọc dữ liệu thất bại ({{status}})',
  exportFailed: 'Xuất file thất bại ({{status}})',
  splitOpenFailed: 'Không mở được phần tách ({{status}})',
  saveFailed: 'Lưu thất bại ({{status}})',
  deleteFailed: 'Xóa thất bại ({{status}})',
  syncFailed: 'Đồng bộ thất bại ({{status}})',
  soSyncFailed: 'Đồng bộ SO thất bại ({{status}})',
  chgNoun: 'bảng',
  chgReading: 'Đang đọc toàn bộ bảng… mất khoảng năm giây.',
  chgGroupAll: 'Tất cả nhà máy',
  chgSearch: 'Tìm HNX PO, mã SAP, mô tả, customer PO…',
  chgColFactory: 'Nhà máy',
  chgColSapCode: 'Mã SAP',
  chgColDescription: 'Mô tả',
  chgColDestination: 'Điểm đến',
  chgColMonth: 'Tháng',
  chgPcs: '{{n}} cái',
};

const zh = {
  subtitle: 'PT 团队的 32 列，由 SAP 和 NetSuite 数据重建，另加 Classification、Currency 和 Price source',
  columnsCount: '{{n}} 列',
  columnsHidden: '{{n}} 列 — {{h}} 列已隐藏',
  syncSap: '同步 SAP',
  syncingSap: '正在同步 SAP…',
  syncNs: '同步 NetSuite',
  syncingNs: '正在同步 NetSuite…',
  addRow: '添加一行',
  columns: '列',
  hiddenTag: '· {{n}} 列隐藏',
  showColumns: '显示列',
  showAll: '全部显示',
  destNames: '目的地名称',
  checkChanges: '检查变更',
  checkChangesHint: '自您上次标记为已核对以来，表格上发生的变更',
  building: '生成中…',
  exportExcel: '导出 Excel',
  intro: '由账簿数据重建的 PT 团队工作簿：同样的 32 列、同样的顺序、同样的表头名称，并在末尾加上 <b>Classification</b>、<b>Currency</b> 和 <b>Price source</b> —— 这些行背后的 PO 以 USD、KRW、JPY 和 EUR 下单，因此价格列旁需要标明币种；成品 PO 在 SAP 中没有价格，因此凡显示价格之处，表格会注明是从哪张 SFP PO 复制的。该文件只是一份<b>列规格说明</b>——不会从中导入任何数据。每一行是一条出库订单行：SAP 侧为 SO 行连接其 PO 行，NetSuite 侧为一条 NS 行，另加尚无销售订单的 SAP PO。',
  legendManualHead: '{{n}} 列由您手工填写 —— 100% 手工。',
  legendManualBody: 'SAP 和 NetSuite 都没有这些数据。点击行上的“编辑”进行填写。',
  legendHybridHead: '{{n}} 列共用。',
  legendHybridBody: '系统填入已有的值并锁定该单元格；系统没有值的单元格由您填写。',
  legendSystemHead: '{{n}} 列来自系统。',
  legendSystemBody: '这些列由 SAP 和 NetSuite 管理 —— 空白表示账簿中尚无数值，而不是有人漏填。',
  newRow: '新行',
  viewRow: '查看 {{key}}',
  editRow: '编辑 {{key}}',
  viewOnly: '仅查看',
  subView: '按表格保存的原样显示所有字段。“编辑”按钮在该行“查看”按钮旁。',
  subNew: '一条不在任何账簿中的行。它会以自己的 <c>MAN-…</c> 键保存，并从此高亮显示，以免手工录入的行被误认为是 SAP 发送的行。没有必填项——有什么填什么。',
  subManual: '这是手工录入的行，所有字段都可以修改。',
  subLedger: '灰色字段来自 SAP 或 NetSuite，显示出来便于您辨认是哪一行。当账簿中已有 <b>Cycle No</b> 和 <b>Customer PO#</b> 时，表单中不会出现这两个字段——它们由导入管理，在此输入的内容会被覆盖。<b>Season</b> 始终由您填写：没有任何导入会写入它。',
  nextAfter: '{{po}} 之后的下一个号码',
  nextAfterGuessed: '{{po}} 之后的下一个号码 —— 编号规则为推测，请核对',
  sysHas: '系统记录为 {{v}}',
  sysHasCpoTyped: '系统记录为 {{v}} —— 此行已被手工输入覆盖；清空输入框即可恢复系统值',
  sysHasDateTyped: '系统记录为 {{v}} —— 此行已被手工输入覆盖',
  sysNoCpo: '系统中此行没有客户 PO',
  sysNoDate: '系统中此行没有日期 —— PO、销售订单和账单都没有',
  deleteRow: '删除行',
  close: '关闭',
  cancel: '取消',
  saving: '保存中…',
  saveRow: '保存行',
  saveChanges: '保存更改',
  searchPh: '搜索 HNX PO / SAP 代码 / SFP / Item# / 条码 / 描述…',
  srcAll: '全部来源',
  srcManual: '手工录入',
  srcPlanned: '计划中 —— 尚无 PO',
  rowsOf: '{{n}} / {{total}} 行',
  clearSortFilters: '清除排序和筛选',
  thNo: '序号',
  thAction: '操作',
  typedSysHas: '手工录入。系统记录为 {{v}}',
  typedNoDate: '手工录入。系统中此行没有日期',
  typedNoCpo: '手工录入。系统中此行没有客户 PO',
  lockedHint: '来自账簿 —— 由导入管理',
  plannedChip: '计划中 · 尚无 PO',
  view: '查看',
  viewHint: '查看此行（只读）',
  plannedHint: '已在 Capacity Management 中计划，尚未在 SAP 或 NetSuite 中下单 —— 请在 Capacity 工作簿中修改，此处会随之更新。',
  fromCapacity: '来自 Capacity',
  edit: '编辑',
  split: '拆分',
  splitTitle: '按目的地拆分此行',
  splitEditHint: '编辑此行的拆分方式',
  del: '删除',
  noRows: '没有行',
  subtotal: '小计 · {{cur}}',
  subtotalNoCur: '小计 · 无币种',
  subtotalRows: '{{n}} 行',
  loadingMore: '正在加载更多…',
  loading: '加载中…',
  destShared: '与 SAP Ship Doc 共用 —— 一个名称，两个页面',
  destScope: 'Master Sheet',
  delAsk: '删除此行？',
  delBody: '只有手工录入的行可以删除，且会被彻底删除 —— 行上的数值不会保存在其他任何地方。',
  delConfirm: '删除该行',
  lineQty: '行数量',
  allocated: '已分配',
  balanced: '已平衡',
  leftToAllocate: '待分配',
  overBy: '超出',
  thDestination: '目的地',
  thQuantity: '数量',
  thStock: '库存',
  stockHint: '尚无目的地的数量',
  notSplit: '尚未拆分 —— 此行仍为一行。',
  addLine: '添加一行',
  putOnStock: '将剩余的 {{n}} 放入库存',
  mustAddUp: '各行合计必须等于 {{n}} 才能保存。',
  saveLines: '保存 {{n}} 行',
  removeSplit: '取消拆分',
  readFailed: '读取失败（{{status}}）',
  exportFailed: '导出失败（{{status}}）',
  splitOpenFailed: '无法打开拆分（{{status}}）',
  saveFailed: '保存失败（{{status}}）',
  deleteFailed: '删除失败（{{status}}）',
  syncFailed: '同步失败（{{status}}）',
  soSyncFailed: 'SO 同步失败（{{status}}）',
  chgNoun: '表格',
  chgReading: '正在读取整个表格… 大约需要五秒。',
  chgGroupAll: '全部工厂',
  chgSearch: '搜索 HNX PO、SAP 代码、描述、客户 PO…',
  chgColFactory: '工厂',
  chgColSapCode: 'SAP 代码',
  chgColDescription: '描述',
  chgColDestination: '目的地',
  chgColMonth: '月份',
  chgPcs: '{{n}} 件',
};

// French and Japanese (task #1173, 2026-11-04) — generated from `en`; a string not yet translated reads English.
const fr = {
  "subtitle": "Les 32 colonnes de l’équipe PT, reconstruites à partir de SAP et NetSuite, plus Classification, Currency et Price source",
  "columnsCount": "{{n}} colonnes",
  "columnsHidden": "{{n}} colonnes — {{h}} masquées",
  "syncSap": "Synchroniser SAP",
  "syncingSap": "Synchronisation de SAP…",
  "syncNs": "Synchroniser NetSuite",
  "syncingNs": "Synchronisation de NetSuite…",
  "addRow": "Ajouter une ligne",
  "columns": "Colonnes",
  "hiddenTag": "· {{n}} masquées",
  "showColumns": "Afficher les colonnes",
  "showAll": "Tout afficher",
  "destNames": "Noms des destinations",
  "checkChanges": "Vérifier les modifications",
  "checkChangesHint": "Ce qui a changé dans la feuille depuis la dernière fois que vous l’avez marquée comme vérifiée",
  "building": "Génération…",
  "exportExcel": "Exporter vers Excel",
  "intro": "Le classeur de l’équipe PT reconstruit à partir des registres : les mêmes 32 colonnes, dans le même ordre et sous les mêmes intitulés, avec <b>Classification</b>, <b>Currency</b> et <b>Price source</b> ajoutées à la fin — les PO derrière ces lignes sont émis en USD, KRW, JPY et EUR, les colonnes de prix ont donc besoin de leur unité à côté, et un PO de produits finis ne porte aucun prix dans SAP : lorsqu’un prix est affiché, la feuille indique de quel PO SFP il a été copié. Le fichier est une <b>spécification de colonnes</b> — rien n’y est importé. Une ligne correspond à une ligne de commande sortante : côté SAP, une ligne de SO jointe à sa ligne de PO ; côté NetSuite, une ligne NS ; plus les PO SAP qui n’ont pas encore de commande client.",
  "legendManualHead": "{{n}} colonnes à saisir — 100 % à la main.",
  "legendManualBody": "Ni SAP ni NetSuite ne les fournissent. Cliquez sur Modifier sur une ligne pour les renseigner.",
  "legendHybridHead": "{{n}} colonnes partagées.",
  "legendHybridBody": "Le système renseigne ce qu’il possède et verrouille la cellule ; là où il n’a rien, la cellule est à vous.",
  "legendSystemHead": "{{n}} colonnes issues du système.",
  "legendSystemBody": "SAP et NetSuite en sont propriétaires — une cellule vide signifie que le registre n’a pas encore de valeur, et non que quelqu’un a oublié.",
  "newRow": "Nouvelle ligne",
  "viewRow": "Consulter {{key}}",
  "editRow": "Modifier {{key}}",
  "viewOnly": "Lecture seule",
  "subView": "Tous les champs tels que la feuille les contient. Modifier se trouve à côté de Consulter sur la ligne.",
  "subNew": "Une ligne qui n’existe dans aucun registre. Elle est enregistrée avec sa propre clé <c>MAN-…</c> et mise en évidence à partir de là, afin qu’une ligne saisie à la main ne soit jamais confondue avec une ligne envoyée par SAP. Rien n’est obligatoire — renseignez ce que vous avez.",
  "subManual": "Une ligne saisie à la main : tous les champs peuvent donc être modifiés.",
  "subLedger": "Les champs grisés proviennent de SAP ou de NetSuite et sont affichés pour que vous sachiez de quelle ligne il s’agit. <b>Cycle No</b> et <b>Customer PO#</b> sont absents du formulaire lorsque le registre les contient déjà — un import en est propriétaire et écraserait toute saisie ici. <b>Season</b> est toujours à vous : aucun import ne l’écrit.",
  "nextAfter": "Suivant après {{po}}",
  "nextAfterGuessed": "Suivant après {{po}} — série déduite, à vérifier",
  "sysHas": "Le système contient {{v}}",
  "sysHasCpoTyped": "Le système contient {{v}} — cette ligne a été saisie par-dessus ; videz la case pour y revenir",
  "sysHasDateTyped": "Le système contient {{v}} — cette ligne a été saisie par-dessus",
  "sysNoCpo": "Le système n’a pas de PO client pour cette ligne",
  "sysNoDate": "Le système n’a pas de date pour cette ligne — le PO, la commande client et la facture n’en portent aucune",
  "deleteRow": "Supprimer la ligne",
  "close": "Fermer",
  "cancel": "Annuler",
  "saving": "Enregistrement…",
  "saveRow": "Enregistrer la ligne",
  "saveChanges": "Enregistrer les modifications",
  "searchPh": "Rechercher PO HNX / code SAP / SFP / Item# / code-barres / description…",
  "srcAll": "Toutes les sources",
  "srcManual": "Saisies à la main",
  "srcPlanned": "Prévues — pas encore de PO",
  "rowsOf": "{{n}} lignes sur {{total}}",
  "clearSortFilters": "Effacer tris et filtres",
  "thNo": "N°",
  "thAction": "Action",
  "typedSysHas": "Saisi à la main. Le système contient {{v}}",
  "typedNoDate": "Saisi à la main. Le système n’a pas de date pour cette ligne",
  "typedNoCpo": "Saisi à la main. Le système n’a pas de PO client pour cette ligne",
  "lockedHint": "Issu du registre — un import en est propriétaire",
  "plannedChip": "Prévue · pas encore de PO",
  "view": "Consulter",
  "viewHint": "Consulter cette ligne (lecture seule)",
  "plannedHint": "Prévue dans la gestion de la capacité, pas encore créée dans SAP ou NetSuite — modifiez-la dans le classeur Capacity ; elle se met à jour ici.",
  "fromCapacity": "Depuis Capacity",
  "edit": "Modifier",
  "split": "Scinder",
  "splitTitle": "Scinder cette ligne par destination",
  "splitEditHint": "Modifier la façon dont cette ligne est scindée",
  "del": "Suppr.",
  "noRows": "Aucune ligne",
  "subtotal": "Sous-total · {{cur}}",
  "subtotalNoCur": "Sous-total · sans devise",
  "subtotalRows": "{{n}} lignes",
  "loadingMore": "Chargement de la suite…",
  "loading": "Chargement…",
  "destShared": "partagé avec SAP Ship Doc — un seul nom, pour les deux écrans",
  "destScope": "la Master Sheet",
  "delAsk": "Supprimer cette ligne ?",
  "delBody": "Seule une ligne saisie à la main peut être supprimée, et elle disparaît complètement — les valeurs qu’elle contient ne sont conservées nulle part ailleurs.",
  "delConfirm": "Supprimer la ligne",
  "lineQty": "Quantité de la ligne",
  "allocated": "Alloué",
  "balanced": "Équilibré",
  "leftToAllocate": "Reste à allouer",
  "overBy": "Dépassement de",
  "thDestination": "Destination",
  "thQuantity": "Quantité",
  "thStock": "Stock",
  "stockHint": "Quantité sans destination pour le moment",
  "notSplit": "Pas encore scindée — cette ligne ne forme qu’une seule ligne.",
  "addLine": "Ajouter une ligne",
  "putOnStock": "Mettre les {{n}} restants en stock",
  "mustAddUp": "Le total des lignes doit être de {{n}} pour pouvoir enregistrer.",
  "saveLines": "Enregistrer {{n}} lignes",
  "removeSplit": "Supprimer la scission",
  "readFailed": "Échec de la lecture ({{status}})",
  "exportFailed": "Échec de l’export ({{status}})",
  "splitOpenFailed": "Impossible d’ouvrir la scission ({{status}})",
  "saveFailed": "Échec de l’enregistrement ({{status}})",
  "deleteFailed": "Échec de la suppression ({{status}})",
  "syncFailed": "Échec de la synchronisation ({{status}})",
  "soSyncFailed": "Échec de la synchronisation des SO ({{status}})",
  "chgNoun": "feuille",
  "chgReading": "Lecture de toute la feuille… cela prend environ cinq secondes.",
  "chgGroupAll": "Toutes les usines",
  "chgSearch": "Rechercher PO HNX, code SAP, description, PO client…",
  "chgColFactory": "Usine",
  "chgColSapCode": "Code SAP",
  "chgColDescription": "Description",
  "chgColDestination": "Destination",
  "chgColMonth": "Mois",
  "chgPcs": "{{n}} pcs"
};
const ja = {
  "subtitle": "PTチームの32列をSAPとNetSuiteから再構築し、Classification、Currency、Price sourceを追加したもの",
  "columnsCount": "{{n}}列",
  "columnsHidden": "{{n}}列 — {{h}}列を非表示",
  "syncSap": "SAPを同期",
  "syncingSap": "SAPを同期中…",
  "syncNs": "NetSuiteを同期",
  "syncingNs": "NetSuiteを同期中…",
  "addRow": "行を追加",
  "columns": "列",
  "hiddenTag": "· {{n}}列を非表示",
  "showColumns": "列を表示",
  "showAll": "すべて表示",
  "destNames": "仕向地名",
  "checkChanges": "変更を確認",
  "checkChangesHint": "前回「確認済み」にしてからシートで変更された内容",
  "building": "作成中…",
  "exportExcel": "Excelにエクスポート",
  "intro": "PTチームのワークブックを台帳から再構築したものです。同じ32列を同じ順序、同じ見出し名で並べ、末尾に<b>Classification</b>、<b>Currency</b>、<b>Price source</b>を追加しています。これらの行の元となるPOはUSD、KRW、JPY、EURで発行されるため、価格の列には単位が必要です。また、完成品のPOにはSAP上に価格がないため、価格を表示している場合は、どのSFP POからコピーしたかをシートに表示します。このファイルは<b>列の仕様</b>であり、ここから何かをインポートすることはありません。1行は出荷オーダーの1明細です。SAP側ではSOラインとPOラインを結合したもの、NetSuite側ではNSの1明細で、さらに受注がまだないSAP POも含みます。",
  "legendManualHead": "手入力の{{n}}列 — 100%手作業です。",
  "legendManualBody": "SAPにもNetSuiteにも、これらの値はまったくありません。行の「編集」を押して入力してください。",
  "legendHybridHead": "共有の{{n}}列。",
  "legendHybridBody": "システムが持っている値は自動で入力され、そのセルはロックされます。システムに値がない場合は、そのセルを入力できます。",
  "legendSystemHead": "システム由来の{{n}}列。",
  "legendSystemBody": "SAPとNetSuiteが管理する列です。空欄は、誰かが入力し忘れたのではなく、台帳にまだ値がないことを意味します。",
  "newRow": "新規行",
  "viewRow": "{{key}}を表示",
  "editRow": "{{key}}を編集",
  "viewOnly": "表示のみ",
  "subView": "シートに保存されている全項目を表示します。編集は、行の「表示」の隣にあります。",
  "subNew": "どの台帳にも存在しない明細です。固有の<c>MAN-…</c>キーを付けて保存され、以降は強調表示されるため、手入力の行がSAPから送られた行と混同されることはありません。必須項目はありません。分かる内容を入力してください。",
  "subManual": "誰かが手入力した行のため、すべての項目を変更できます。",
  "subLedger": "グレーの項目はSAPまたはNetSuiteの値で、どの行かを判別できるように表示しています。<b>Cycle No</b>と<b>Customer PO#</b>は、台帳にすでに値がある場合はフォームに表示されません。インポートが管理する項目で、ここで入力しても上書きされるためです。<b>Season</b>は常に手入力です。インポートでは書き込まれません。",
  "nextAfter": "{{po}}の次",
  "nextAfterGuessed": "{{po}}の次 — 系列を推測しています。確認してください",
  "sysHas": "システムの値：{{v}}",
  "sysHasCpoTyped": "システムの値：{{v}} — この行は手入力で上書きされています。元に戻すには入力欄を空にしてください",
  "sysHasDateTyped": "システムの値：{{v}} — この行は手入力で上書きされています",
  "sysNoCpo": "この明細にはシステム上のお客様PO番号がありません",
  "sysNoDate": "この明細にはシステム上の日付がありません。PO、受注、請求のいずれにも日付がありません",
  "deleteRow": "行を削除",
  "close": "閉じる",
  "cancel": "キャンセル",
  "saving": "保存中…",
  "saveRow": "行を保存",
  "saveChanges": "変更を保存",
  "searchPh": "HNX PO / SAPコード / SFP / Item# / バーコード / 説明を検索…",
  "srcAll": "すべてのソース",
  "srcManual": "手入力",
  "srcPlanned": "計画 — POなし",
  "rowsOf": "{{total}}行中{{n}}行",
  "clearSortFilters": "並べ替えとフィルターをクリア",
  "thNo": "No",
  "thAction": "操作",
  "typedSysHas": "手入力。システムの値：{{v}}",
  "typedNoDate": "手入力。この明細にはシステム上の日付がありません",
  "typedNoCpo": "手入力。この明細にはシステム上のお客様PO番号がありません",
  "lockedHint": "台帳の値です — インポートが管理しています",
  "plannedChip": "計画 · POなし",
  "view": "表示",
  "viewHint": "この行を表示（読み取り専用）",
  "plannedHint": "キャパシティ管理で計画済みですが、SAPやNetSuiteではまだ発行されていません。変更はCapacityのワークブックで行ってください。ここに反映されます。",
  "fromCapacity": "Capacityより",
  "edit": "編集",
  "split": "分割",
  "splitTitle": "この明細を仕向地別に分割",
  "splitEditHint": "この明細の分割内容を編集",
  "del": "削除",
  "noRows": "行がありません",
  "subtotal": "小計 · {{cur}}",
  "subtotalNoCur": "小計 · 通貨なし",
  "subtotalRows": "{{n}}行",
  "loadingMore": "さらに読み込み中…",
  "loading": "読み込み中…",
  "destShared": "SAP Ship Docと共有 — 名前は1つで、両方の画面に反映されます",
  "destScope": "Master Sheet",
  "delAsk": "この行を削除しますか？",
  "delBody": "削除できるのは手入力された行だけで、完全に削除されます。行の値は他のどこにも保存されません。",
  "delConfirm": "行を削除",
  "lineQty": "明細の数量",
  "allocated": "配分済み",
  "balanced": "一致",
  "leftToAllocate": "未配分",
  "overBy": "超過",
  "thDestination": "仕向地",
  "thQuantity": "数量",
  "thStock": "在庫",
  "stockHint": "まだ仕向地が決まっていない数量",
  "notSplit": "まだ分割されていません。この明細は1行のままです。",
  "addLine": "明細を追加",
  "putOnStock": "残りの{{n}}を在庫にする",
  "mustAddUp": "保存するには、明細の合計を{{n}}にする必要があります。",
  "saveLines": "{{n}}明細を保存",
  "removeSplit": "分割を解除",
  "readFailed": "読み込みに失敗しました（{{status}}）",
  "exportFailed": "エクスポートに失敗しました（{{status}}）",
  "splitOpenFailed": "分割を開けませんでした（{{status}}）",
  "saveFailed": "保存に失敗しました（{{status}}）",
  "deleteFailed": "削除に失敗しました（{{status}}）",
  "syncFailed": "同期に失敗しました（{{status}}）",
  "soSyncFailed": "SOの同期に失敗しました（{{status}}）",
  "chgNoun": "シート",
  "chgReading": "シート全体を読み込んでいます… 約5秒かかります。",
  "chgGroupAll": "すべての工場",
  "chgSearch": "HNX PO、SAPコード、説明、お客様POを検索…",
  "chgColFactory": "工場",
  "chgColSapCode": "SAPコード",
  "chgColDescription": "説明",
  "chgColDestination": "仕向地",
  "chgColMonth": "月",
  "chgPcs": "{{n}} pcs"
};

const dictionary = { ns: 'xMaster', en, ko, vi, zh, fr, ja };

export default dictionary;
